// AI client — single entry point for all calls to the /story Lambda.
//
// Why this exists: Amplify `post({ apiName: 'remnantAIApi' })` was not
// actually IAM-signing requests in our deployment, so the Lambda received
// every call (including sensitive actions like follow_up / extract_themes)
// with no auth context — verified anonymously reachable 2026-05-05. TB-040.
//
// This helper attaches a Cognito ID token as `Authorization: Bearer <jwt>`
// for any action that is not in PUBLIC_AI_ACTIONS, switching all auth-
// required AI calls to a single, explicitly-authenticated path. The Lambda
// validates the JWT via Cognito JWKS for non-public actions.
//
// Public actions (transcribe, expand) are also called by the unauthenticated
// ContributePage — they pass through without a JWT. Cost-burn protection for
// those actions relies on per-invite contribution caps and can be tightened
// later via API Gateway throttling.

import { fetchAuthSession } from 'aws-amplify/auth';
import { AI_API } from '../constants/config';

const PUBLIC_AI_ACTIONS = new Set(['transcribe', 'expand']);

// AI_API is the full URL to `/dev/story` — strip the trailing `/story`
// to derive the base URL so we can target other paths (e.g. `/feedback`)
// on the same Lambda + same API Gateway stage.
const AI_API_BASE = AI_API.replace(/\/story$/, '');

/**
 * Call the AI Lambda. Returns the parsed JSON body (e.g. `{ text: "..." }`).
 * Throws on non-2xx response or `data.error`.
 *
 * @param {object} body  Request body, must include `action` plus action-specific fields.
 * @param {object} [opts]  Optional config: `{ path }` to target a path other than `/story`.
 * @returns {Promise<object>} Parsed JSON response.
 */
export async function callAi(body, { path = '/story' } = {}) {
  if (!body || !body.action) {
    throw new Error('callAi: body.action is required');
  }

  const headers = { 'Content-Type': 'application/json' };

  // Always attach a Cognito ID token if the user is signed in. This is
  // required for non-public actions; benign for public actions and
  // forward-compatible with future per-user rate limiting.
  let idToken = null;
  try {
    const session = await fetchAuthSession();
    idToken = session?.tokens?.idToken?.toString() || null;
  } catch (e) {
    // No session — only OK for public actions. If this is a non-public
    // action, the Lambda will reject with 401 and we surface that below.
  }

  if (idToken) {
    headers.Authorization = `Bearer ${idToken}`;
  } else if (!PUBLIC_AI_ACTIONS.has(body.action)) {
    // Fail fast with a clear client-side error — saves a network round-trip
    // and gives a better surface for UI handling.
    throw new Error('Sign-in required for this action.');
  }

  const resp = await fetch(`${AI_API_BASE}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  if (resp.status === 401) {
    throw new Error('Authentication required.');
  }
  // TB-085: rate-limit responses carry a friendly, user-facing message + code.
  // Surface that message verbatim (the per-user limit names support@) and
  // attach `.code` so callers can distinguish a daily limit (USER_LIMIT) from
  // a transient capacity stop (PLATFORM_LIMIT) if they want to.
  if (resp.status === 429 || resp.status === 503) {
    let msg = resp.status === 503
      ? 'Our AI is briefly at capacity. Please try again in a little while.'
      : "You've reached today's limit. Reach out to support@taleboundbook.com if you need more.";
    let code = resp.status === 503 ? 'PLATFORM_LIMIT' : 'USER_LIMIT';
    try {
      const errBody = await resp.json();
      if (errBody && errBody.error) msg = errBody.error;
      if (errBody && errBody.code) code = errBody.code;
    } catch (e) { /* keep the default message */ }
    const err = new Error(msg);
    err.code = code;
    err.status = resp.status;
    throw err;
  }
  if (!resp.ok) {
    const errBody = await resp.text().catch(() => '');
    const err = new Error(`AI request failed (${resp.status}): ${errBody.slice(0, 200)}`);
    // F-415: carry the status. Without it a caller cannot tell an API Gateway cut (504, work still
    // running) from a real Lambda 500 (work dead), and the assembler catch treated both as a failed
    // story. Only 429/503 carried a status before this.
    err.status = resp.status;
    throw err;
  }

  const data = await resp.json();
  if (data && data.error) {
    throw new Error(data.error);
  }
  return data;
}

// ── F-415: surviving the 29-second API Gateway cut ─────────────────────────
//
// `/story` is an AWS_PROXY integration with `timeoutInMillis: 29000`, which is the account-level
// maximum (quota L-E5AE38E3, adjustable by request). When a Claude call runs longer than that, API
// Gateway closes the connection and returns a 504 while the Lambda keeps running to a clean finish.
//
// Twice in 14 days that destroyed a story that had been written: 2026-08-07 (32.7s) and 2026-08-08
// (44.3s). They were the only two Claude calls over 29s out of 198. The teller was shown "your story
// was not written" while the story sat in a Lambda that was still 15 seconds from returning it.
//
// The fix is not a longer timeout — we do not control that number from here, and a longer one only
// moves the cliff. It is to make the work RESUMABLE: the Lambda records the job under a client-chosen
// `jobKey` before it calls Claude and stores the result when it finishes, so a client that loses the
// connection can ask for the same job instead of paying for a second 44-second call that would be cut
// at exactly the same place.

/**
 * Poll interval and total wait. The worst observed generation was 44.3s; this allows ~3.4x that.
 *
 * The budget must sit ABOVE the job store's `JOB_RUNNING_MAX_SECONDS` (135s), because giving up first
 * would mean the honest failure we report is a guess rather than an answer from the store — and would
 * abandon a story the Lambda is still allowed to be writing. That ordering is enforced across all
 * three files by `scripts/generation-ceiling.test.mjs`.
 */
const RESUME_POLL_MS = 2500;
// Exported because the polish pair's own budget must sit ABOVE it (polishRace.js). Two budgets that
// can silently cross each other is how a resume becomes decorative, so they are coupled in code.
export const RESUME_BUDGET_MS = 150000;

/**
 * How long a `missing` job is forgiven before it counts as "never started".
 *
 * The Lambda writes the job row in `claimJob`, which runs AFTER JWKS validation and the rate-limit
 * check. So there is a real window — a connection dropped early, or a cold start — where we poll
 * before the Lambda has claimed. Treating that first `missing` as terminal would be a NEW way to
 * lose a story, invented by the fix meant to stop losing them.
 */
const RESUME_MISSING_GRACE_MS = 15000;

/**
 * A key identifying ONE generation attempt. Reusing it is what lets a lost connection collect the
 * work it started; a fresh one starts fresh work. Must satisfy the Lambda's `JOB_KEY_RE`
 * (`[A-Za-z0-9_-]{8,128}`), since it becomes part of a DynamoDB partition key.
 */
export function newJobKey(prefix = 'job') {
  const rand = (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID().replace(/-/g, '')
    : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
  return `${prefix}-${rand}`.slice(0, 128);
}

/**
 * Is this failure one where the server may still be working?
 *
 * ONLY a gateway cut or a dropped connection. A 429/503 is a deliberate refusal and a 4xx is our own
 * bad request — polling those burns the teller's wait for nothing and hides the real message.
 */
export function isResumableError(err) {
  if (!err) return false;
  if (err.status === 502 || err.status === 504) return true;
  // `fetch` rejects with a TypeError (no status) when the connection drops mid-flight — which is the
  // other way the same 29s cut can surface, depending on the browser.
  if (typeof err.status !== 'number' && err instanceof TypeError) return true;
  return false;
}

/**
 * Read a `job_result` response. Fails CLOSED: anything not recognisably finished-with-text is
 * 'missing', because reporting a job done with no story is the exact failure PIPE-OUT-1 exists to
 * refuse, and it is worse than an honest failure.
 *
 * F-415-STREAM: a `running` response may carry `partial` — the text the Lambda has streamed onto
 * the job row so far. It is surfaced ONLY inside the running state; nothing here or downstream may
 * ever promote a partial to `done`. A half-written chapter mistaken for a finished story is the
 * worst outcome this pipeline has, worse than the silent wait the stream replaces.
 */
export function classifyJobResponse(data) {
  if (!data || typeof data !== 'object') return { state: 'missing' };
  if (data.status === 'running') {
    return (typeof data.partial === 'string' && data.partial.trim())
      ? { state: 'running', partial: data.partial }
      : { state: 'running' };
  }
  if (data.status === 'done' && typeof data.text === 'string' && data.text.trim()) {
    return { state: 'done', text: data.text };
  }
  return { state: 'missing' };
}

/**
 * `callAi`, but able to survive losing the connection to a call that is still running.
 *
 * `jobKey` is chosen by the caller and identifies ONE attempt. Reusing it is what makes the resume
 * work; a fresh one starts fresh work. Without `onPartial` a call that answers inside 29 seconds
 * (197 of the last 198) never polls at all — the poll loop is a fallback for a cut connection.
 *
 * F-415-STREAM: pass `onPartial(text)` and the poll loop becomes a CHANNEL instead of a fallback —
 * it runs from t0 alongside the initial call, the request carries `streamPartials: true` so the
 * Lambda writes accumulating text onto the job row, and every poll that finds more text than the
 * last surfaces it. Emissions are monotonic (a stale replica read never shows the teller their
 * story shrinking) and a throwing `onPartial` is swallowed: display is never load-bearing.
 *
 * F-416-RESUME-CLOCK-STARTS-LATE: the budget clock starts at t0, BEFORE the initial call — not
 * after it settles. The old clock let a call cut at 29s poll until 29s + budget, which put the
 * resume's real deadline PAST the polish pair's 155s budget, so the pair could preempt the very
 * resume it exists to wait for. From t0, `RESUME_BUDGET_MS < POLISH_PAIR_BUDGET_MS` means what it
 * says. Proven composed in polishRace.test.js, not by comparing the two constants.
 *
 * Throws `GENERATION_JOB_MISSING` if the work never started and `GENERATION_RESUME_TIMEOUT` if it is
 * still running at the budget. Both are honest failures the caller should surface, not hide.
 */
export async function callAiResumable(body, opts = {}) {
  const {
    path = '/story',
    jobKey,
    pollMs = RESUME_POLL_MS,
    budgetMs = RESUME_BUDGET_MS,
    missingGraceMs = RESUME_MISSING_GRACE_MS,
    sleep = ms => new Promise(r => setTimeout(r, ms)),
    now = () => Date.now(),
    onPartial = null,
  } = opts;

  if (!jobKey) throw new Error('callAiResumable: jobKey is required');

  const startedAt = now(); // t0 — before the initial call, see the F-416 note above

  // In streaming mode the poll loop and the initial call race; whichever produces a final answer
  // first wins and the loser is ignored. `finished` stops the loop from polling on after a win.
  let finished = false;
  let initialInFlight = true;

  const initialBody = onPartial ? { ...body, jobKey, streamPartials: true } : { ...body, jobKey };
  const initialCall = (async () => {
    try {
      const data = await callAi(initialBody, { path });
      return { kind: 'data', data };
    } catch (err) {
      return { kind: 'error', err };
    } finally {
      initialInFlight = false;
    }
  })();

  const pollLoop = async () => {
    let lastPartialLen = 0;
    while (!finished && now() - startedAt < budgetMs) {
      await sleep(pollMs);
      if (finished) break;
      let result;
      try {
        result = await callAi({ action: 'job_result', jobKey }, { path });
      } catch (err) {
        // A poll that fails transiently is not a verdict on the job; keep waiting out the budget. A
        // real refusal (rate limit, auth) still stops here, same as the first call.
        if (!isResumableError(err)) throw err;
        continue;
      }
      const { state, text, partial } = classifyJobResponse(result);
      if (state === 'done') return { text };
      if (state === 'running' && onPartial && partial && partial.length > lastPartialLen) {
        lastPartialLen = partial.length;
        try { onPartial(partial); } catch (e) { /* display is never load-bearing */ }
      }
      // `missing` is only believed once the Lambda has had time to claim (see RESUME_MISSING_GRACE_MS)
      // AND the initial call is no longer a possible source of the answer. While it is still in
      // flight, a missing row may just be a slow claim on a live call — killing the generation on it
      // would be a NEW way to lose a story, invented by the streaming that was meant to soften the wait.
      if (state === 'missing' && !initialInFlight && now() - startedAt >= missingGraceMs) {
        throw new Error('GENERATION_JOB_MISSING');
      }
    }
    if (finished) return new Promise(() => {}); // a winner already resolved the outer promise
    throw new Error('GENERATION_RESUME_TIMEOUT');
  };

  // How the initial response resolves, shared by both modes. Returns null when the poll loop owns
  // the outcome: a 200 `{pending:true}` (the Lambda found this job already in flight) or a
  // resumable failure (gateway cut / dropped connection — the Lambda is still working).
  const settleInitial = (r) => {
    if (r.kind === 'data') {
      if (r.data && r.data.pending) return null;
      return { data: r.data };
    }
    if (!isResumableError(r.err)) throw r.err;
    return null;
  };

  if (!onPartial) {
    // Fallback mode (assembler): no polls unless the connection was lost. Identical to the original
    // shape except that the budget above was started at t0.
    const r = await initialCall;
    const settled = settleInitial(r);
    if (settled) return settled.data;
    return pollLoop();
  }

  // Streaming mode (the Whole): poll from t0 as a channel.
  return new Promise((resolve, reject) => {
    const win = v => { if (!finished) { finished = true; resolve(v); } };
    const lose = e => { if (!finished) { finished = true; reject(e); } };
    initialCall.then(r => {
      let settled;
      try { settled = settleInitial(r); } catch (err) { lose(err); return; }
      if (settled) win(settled.data);
      // null → the poll loop carries the outcome
    });
    pollLoop().then(win, lose);
  });
}

/**
 * Fire-and-forget beacon: record that a TEXT interview started (activation-
 * funnel metric — "spoke or wrote with Pip" vs "saved a story"). Never throws
 * and never blocks the UI; a logging failure must not disrupt the interview.
 * Voice interviews are logged server-side when the voice token is minted, so
 * this is only fired from the text interview entry points.
 */
export function logInterviewStart() {
  try {
    callAi({ action: 'interview_started' }).catch(() => {});
  } catch (e) { /* never disrupt the interview */ }
}

/**
 * Fire-and-forget beacon: record that the storyteller REACHED STORY GENERATION
 * (finished the interview; Pip is writing the story). Activation-funnel midpoint
 * between "started an interview" and "saved a story" — it isolates whether people
 * quit mid-interview or reach their story but don't keep it. Covers voice + text,
 * since generation runs client-side for both. Never throws, never blocks the UI.
 */
export function logReachedGeneration(heavy = false) {
  try {
    // F-24: durable heavy-content flag (boolean only, no content) so the weekly report can compare
    // save/order rates for heavy-content tellers (the companion-user hypothesis).
    callAi({ action: 'interview_generated', heavy: !!heavy }).catch(() => {});
  } catch (e) { /* never disrupt generation */ }
}

/**
 * Fire-and-forget: a generation FAILED, and the founder hears about it immediately (F-334/F-335).
 *
 * WHY THIS EXISTS. Between 07-02 and 08-04 at least five real tellers hit the stall landing and three
 * more got a substituted version, and not one of those was visible to anyone: the failure was a toast
 * that faded, and nothing was logged ([V-333] Step 5). Silence read exactly like success.
 *
 * `cause` is one of GENERATION_FAIL_CAUSES (generationTimeout.js documents which leg emits which):
 * 'pair_timeout' | 'assembler_fail' | 'empty_output' | 'polish_timeout' | 'polish_fail'.
 * NO story content, NO transcript, NO answer text is ever sent — the cause enum only. The Lambda logs
 * it structurally and sends the founder alert. Never throws, never blocks, never delays the banner.
 */
export function logGenerationFailed(cause) {
  try {
    callAi({ action: 'generation_failed', cause: String(cause || 'unknown') }).catch(() => {});
  } catch (e) { /* a failing alert must never make the failure worse */ }
}
